import { Injectable, computed, effect, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { ApiService } from '@frontend/sports/common/api-utils';
import { LoggerFactory } from '@frontend/sports/common/core/feature/logging';
import { SimpleCallback } from '@frontend/sports/common/core/utils/extended-types';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { UserService } from '@frontend/sports/user/feature';
import { BehaviorSubject, catchError, map, merge, of, startWith } from 'rxjs';

import { AlertConfigService } from './alert-config.service';
import { NativeAlertsResponse, Subscription } from './model';

@Injectable({ providedIn: 'root' })
export class SubscriptionStreamService {
    private readonly apiService = inject(ApiService);
    private readonly userService = inject(UserService);
    private readonly timerService = inject(TimerService);
    private readonly alertConfig = inject(AlertConfigService);
    private readonly logger = inject(LoggerFactory).getLogger('SubscriptionStreamService');

    private readonly _data$ = new BehaviorSubject<Subscription[]>([]);
    readonly data$ = this._data$.asObservable();
    private interval?: SimpleCallback;

    readonly isUserAuthenticated = toSignal(
        merge(this.userService.onUserLogin$.pipe(takeUntilDestroyed()), this.userService.onUserLogout$.pipe(takeUntilDestroyed())).pipe(
            startWith(this.userService.isAuthenticated),
            map(() => this.userService.isAuthenticated),
        ),
        { initialValue: this.userService.isAuthenticated },
    );

    readonly isSubscriptionEnabled = computed(() => this.alertConfig.alertsEnabled() && this.isUserAuthenticated());

    constructor() {
        effect(() => {
            if (this.isSubscriptionEnabled()) {
                this.startGettingSubscriptions(this.alertConfig.fetchingInterval());
            } else {
                this.stopGettingSubscriptions();
            }
        });
    }

    stopGettingSubscriptions(): void {
        if (this.interval) {
            this.interval();
        }

        this._data$.next([]);
    }

    private startGettingSubscriptions(fetchingInterval: number): void {
        if (this.interval) {
            this.interval();
        }

        this.refreshSubscriptions();

        this.interval = this.timerService.setIntervalOutsideAngular(() => {
            this.refreshSubscriptions();
        }, fetchingInterval);
    }

    private refreshSubscriptions(): void {
        this.apiService
            .get<NativeAlertsResponse>('nativealerts/getSubscriptions')
            .pipe(
                catchError((err) => {
                    this.logger.error(err, 'Refresh Native Alerts Subscriptions failed.');

                    return of({ subscription: [] });
                }),
            )
            .subscribe((response) => this._data$.next(response.subscription));
    }
}
